import { useCallback } from 'react'

import { ToolWidgetType, WidgetNode } from '@/shared/components/Mosaic'
import { useToastContext } from '@/shared/components/Toast/ToastContext'

type SetWidgetsType = (items: WidgetNode[]) => void

/**
 * The following hook help bootstrap the common actions logic for the widget toolbar such as reordering, adding, and removing widgets.
 * 
 * @param widgets the current state of the widgets in the toolbar
 * @param setWidgets a dispatch function to update the widgets in the toolbar
 * @returns 
 */
export const useWidgetActions = (widgets: WidgetNode[], setWidgets: SetWidgetsType) => {
  const { setToastContent } = useToastContext()

  // Set given widget from mosaic to toolbar
  const moveWidgetToToolbar = useCallback((widgetType: ToolWidgetType, idx: number) => {
    const widgetIndex = widgets.findIndex(widget => widget.widgetType === widgetType && widget.location === 'mosaic')

    if (idx === -1) {
      // Re-insert the widget back to the original position
      widgets[widgetIndex].location = 'toolbar'
      setWidgets([...widgets])
    } else if (widgetIndex !== -1) {
      const updatedWidget: WidgetNode = {
        ...widgets[widgetIndex],
        location: 'toolbar',
      }
      const newWidgets = widgets.filter((_, index) => index !== widgetIndex)
      const updatedWidgets: WidgetNode[] = [...newWidgets.slice(0, idx), updatedWidget, ...newWidgets.slice(idx)]

      setWidgets(updatedWidgets)
    }
  }, [widgets, setWidgets])

  // Set widget from a specific index location to mosaic
  const moveWidgetToWindow = useCallback((idx: number) => {
    const toolbarWidgets: WidgetNode[] = []
    const holdOutWidget: (WidgetNode & { id: number })[] = []

    for (let i = 0; i < widgets.length; i++) {
      if (widgets[i].location === 'toolbar') {
        toolbarWidgets.push(widgets[i])
      } else {
        holdOutWidget.push({ ...widgets[i], id: i })
      }
    }

    toolbarWidgets[idx].location = 'mosaic'

    // Re-insert hold out widgets into the toolbar widgets
    const finalWidgets = toolbarWidgets

    for (let i = 0; i < holdOutWidget.length; i++) {
      const { id, ...rest } = holdOutWidget[i]

      finalWidgets.splice(id, 0, rest)
    }

    setWidgets(finalWidgets)
  }, [widgets, setWidgets])

  // Move widget in fromIndex to toIndex position
  const moveWidgetAroundToolbar = useCallback((fromIndex: number, toIndex: number) => {
    const toolbarWidgets: WidgetNode[] = []
    const holdOutWidget: (WidgetNode & { id: number })[] = []

    for (let i = 0; i < widgets.length; i++) {
      if (widgets[i].location === 'toolbar') {
        toolbarWidgets.push(widgets[i])
      } else {
        holdOutWidget.push({ ...widgets[i], id: i })
      }
    }

    if (fromIndex === toIndex)
      return

    const movingWidget = toolbarWidgets[fromIndex]
    let newToolbarWidgets = toolbarWidgets.filter((_, index) => index !== fromIndex)

    if (toIndex > fromIndex) toIndex--

    newToolbarWidgets = [...newToolbarWidgets.slice(0, toIndex), movingWidget, ...newToolbarWidgets.slice(toIndex)]

    // Re-insert hold out widgets into the toolbar widgets
    const finalWidgets = newToolbarWidgets

    for (let i = 0; i < holdOutWidget.length; i++) {
      const { id, ...rest } = holdOutWidget[i]

      finalWidgets.splice(id, 0, rest)
    }

    setWidgets(finalWidgets)
  }, [widgets, setWidgets])

  // Hide the widget from the layout
  const hideWidget = useCallback((widgetType: ToolWidgetType, idx?: number) => {
    let widgetIndex = widgets.findIndex(widget => widget.widgetType === widgetType && widget.location === 'toolbar')

    if (idx) {
      // If the idx is provided, find the toolbar widget at the index
      let toolWidgetIdx = -1

      for (let i = 0; i < widgets.length; i++) {
        if (widgets[i].location === 'toolbar') {
          toolWidgetIdx += 1
        }

        if (toolWidgetIdx === idx) {
          widgetIndex = i
          break
        }
      }
    }

    if (widgetIndex !== -1) {
      const updatedWidget: WidgetNode = {
        ...widgets[widgetIndex],
        location: 'hidden',
      }
      const updatedWidgets: WidgetNode[] = [...widgets.slice(0, widgetIndex), updatedWidget, ...widgets.slice(widgetIndex + 1)]

      setWidgets(updatedWidgets)

      return
    }

    setToastContent({
      open: true,
      severity: 'error',
      title: 'Window Not Closed',
      content: 'Close the window before removing it from the toolbar.',
    })
  }, [widgets, setWidgets, setToastContent])

  const unHideWidget = useCallback((widgetType: ToolWidgetType) => {
    const widgetIndex = widgets.findIndex(widget => widget.widgetType === widgetType && widget.location === 'hidden')

    if (widgetIndex !== -1) {
      const updatedWidget: WidgetNode = {
        ...widgets[widgetIndex],
        location: 'toolbar',
      }
      const updatedWidgets: WidgetNode[] = [...widgets.slice(0, widgetIndex), updatedWidget, ...widgets.slice(widgetIndex + 1)]

      setWidgets(updatedWidgets)
    }
  }, [widgets, setWidgets])

  return {
    moveWidgetToToolbar,
    moveWidgetAroundToolbar,
    moveWidgetToWindow,
    hideWidget,
    unHideWidget,
  }
}